import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Save, Trash2, ArrowLeft, Phone, X, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

export default function SupplierForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Two-step popup shown when Manual Add hits a mobile number already on KonexoCRM.
  const [waitDialogOpen, setWaitDialogOpen] = useState(false);
  const [waitStep, setWaitStep] = useState("sent"); // "sent" -> "waiting"
  const [waitTargetName, setWaitTargetName] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    apiClient.get(`/suppliers/${id}`)
      .then((r) => setForm({ name: r.data.name, phone: r.data.phone, notes: r.data.notes || "" }))
      .catch(() => toast.error("Supplier not found"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), phone: form.phone.trim(), notes: form.notes.trim() };
      if (isEdit) {
        await apiClient.put(`/suppliers/${id}`, payload);
        toast.success("Supplier updated");
        navigate("/businesses");
        return;
      }
      // Create — backend now returns an envelope. If the phone belongs to a
      // registered KonexoCRM business we don't create a duplicate and instead
      // show the two-step "Connection request sent / Waiting" popup.
      const { data } = await apiClient.post(`/suppliers`, payload);
      if (data?.status === "connection_requested" || data?.status === "already_connected") {
        setWaitTargetName(data.target_business_name || form.name.trim());
        setWaitStep(data.status === "already_connected" ? "already" : "sent");
        setWaitDialogOpen(true);
        return; // don't navigate away — user closes the popup with ✕
      }
      toast.success("Supplier added");
      navigate("/businesses");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const closeWaitDialog = () => {
    setWaitDialogOpen(false);
    navigate("/businesses");
  };

  const onDelete = async () => {
    try { await apiClient.delete(`/suppliers/${id}`); toast.success("Deleted"); navigate("/suppliers"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <>
      <AppHeader title={isEdit ? "Edit Supplier" : "Add Supplier"} subtitle="Private network · only you can see" />
      <div className="px-4 pt-4 pb-28">
        <button onClick={() => navigate(-1)} data-testid="supplier-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {loading ? <div className="text-center text-slate-500 py-10">Loading...</div> : (
          <form onSubmit={onSubmit} data-testid="supplier-form" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Supplier Name *</Label>
              <Input id="name" data-testid="supplier-input-name" value={form.name} onChange={set("name")} placeholder="e.g., Mahesh Wholesalers" required className="h-14 rounded-xl text-base" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone *</Label>
              <div className="relative">
                <Phone className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="phone" data-testid="supplier-input-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="98765 43210" required className="h-14 pl-12 rounded-xl text-base" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" data-testid="supplier-input-notes" value={form.notes} onChange={set("notes")} rows={4} placeholder="What do they supply? Payment terms?" className="rounded-xl text-base" />
            </div>
            <Button type="submit" disabled={saving} data-testid="supplier-save-btn" className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-lg font-semibold gap-2">
              <Save className="w-5 h-5" /> {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Supplier"}
            </Button>
            {isEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="ghost" data-testid="supplier-delete-btn" className="w-full h-12 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl font-semibold gap-2">
                    <Trash2 className="w-5 h-5" /> Delete Supplier
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-sm rounded-2xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this supplier?</AlertDialogTitle>
                    <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDelete} data-testid="supplier-confirm-delete" className="bg-red-600 hover:bg-red-700 rounded-xl">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </form>
        )}
      </div>

      {/* Manual-Add → phone already on KonexoCRM → two-step popup (spec §1). */}
      <Dialog open={waitDialogOpen} onOpenChange={(o) => { if (!o) closeWaitDialog(); }}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="manual-add-waiting-dialog">
          {waitStep === "sent" && (
            <>
              <DialogHeader>
                <DialogTitle>Business already on KonexoCRM</DialogTitle>
                <DialogDescription>
                  This business already exists on KonexoCRM. A connection request has been sent.
                </DialogDescription>
              </DialogHeader>
              <Button
                onClick={() => setWaitStep("waiting")}
                data-testid="manual-add-continue"
                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Continue
              </Button>
            </>
          )}
          {waitStep === "waiting" && (
            <>
              <DialogHeader className="pr-8">
                <DialogTitle className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> Waiting for approval</DialogTitle>
                <DialogDescription>
                  Waiting for {waitTargetName || "the business"} to approve your connection request.
                  You&apos;ll see them under Connected Businesses once accepted.
                </DialogDescription>
              </DialogHeader>
              <button
                type="button"
                onClick={closeWaitDialog}
                data-testid="manual-add-close"
                className="absolute right-4 top-4 w-8 h-8 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
          {waitStep === "already" && (
            <>
              <DialogHeader>
                <DialogTitle>Already connected</DialogTitle>
                <DialogDescription>
                  You&apos;re already connected with {waitTargetName || "this business"} on KonexoCRM.
                </DialogDescription>
              </DialogHeader>
              <Button
                onClick={closeWaitDialog}
                data-testid="manual-add-ok"
                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Ok
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
